import type {
  IResearchTopic2025Schema,
  IResearchTopicBasicInfo2025Schema,
  IAuthor2025Schema,
  IRTJournal2025Schema
} from "~/types/models/ResearchTopic2025.model";
import type {
  IResearchTopicSchema,
  IResearchTopicBasicInfoSchema,
  IRTJournalSchema
} from "~/types/models/ResearchTopic.model";
import type { IAuthorSchema } from "~/types/models/Author.model";
import type { IAffiliationsSchema } from "~/types/models/Affiliations.model";

/**
 * Maps a 2025 author schema to the legacy author schema format
 */
function mapAuthor2025ToLegacy(author2025: IAuthor2025Schema): IAuthorSchema {
  const affiliation2025 = author2025.Affiliation;

  const affiliations: IAffiliationsSchema[] = affiliation2025
    ? [
        {
          AddressLine1: affiliation2025.AddressLine1,
          AddressLine2: affiliation2025.AddressLine2,
          AddressLine3: affiliation2025.AddressLine3,
          City: affiliation2025.City,
          Country: affiliation2025.Country,
          Department: affiliation2025.Department,
          Organization: affiliation2025.Organization,
          Position: affiliation2025.Position,
          PostalCode: affiliation2025.PostalCode,
          State: affiliation2025.State
        }
      ]
    : [];

  return {
    UserId: author2025.UserId,
    FullName: author2025.FullName,
    Email: author2025.EmailId,
    Affiliations: affiliations
  };
}

/**
 * Maps a 2025 journal schema to the legacy journal schema format
 */
function mapJournal2025ToLegacy(
  journal2025: IRTJournal2025Schema
): IRTJournalSchema {
  return {
    JournalId: journal2025.JournalId,
    Title: journal2025.Title,
    IsPrimary: journal2025.IsPrimary,
    SpecialtyId: journal2025.SpecialtyId || 0,
    Sections: (journal2025.Sections || []).map((section) => ({
      SectionId: section.SectionId,
      Title: section.Title,
      IsPrimary: section.IsPrimary
    })) as [{ SectionId: number; Title?: string; IsPrimary?: boolean }]
  };
}

/**
 * Maps a 2025 research topic schema to the legacy schema format
 */
export function mapResearchTopic2025ToLegacy(
  rt2025: IResearchTopic2025Schema
): IResearchTopicSchema {
  const sortedCoordinators = rt2025.TopicCoordinators.sort(
    (a, b) => a.SequenceId - b.SequenceId
  );
  const sortedEditors = rt2025.TopicEditors.sort(
    (a, b) => a.SequenceId - b.SequenceId
  );

  return {
    ResearchTopicId: rt2025.ResearchTopicId,
    Slug: rt2025.Slug,
    Title: rt2025.Title,
    IsMagazinePage: rt2025.IsMagazinePage,
    AbstractSubmissionDeadline: String(rt2025.AbstractSubmissionDeadline),
    ClosedOn: rt2025.ClosedOn,
    OnlineSince: rt2025.OnlineSince,
    CreatedDate: String(rt2025.CreatedDate),
    Description: rt2025.Description,
    ImageUrl: rt2025.ImageUrl,
    ImpactV5: rt2025.ImpactV5,
    IsOpenForSubmission: rt2025.IsOpenForSubmission,
    Journals: (rt2025.Journals || []).map(mapJournal2025ToLegacy) as [
      IRTJournalSchema
    ],
    Keywords: rt2025.Keywords || [],
    ManuscriptSubmissionDeadline: String(rt2025.ManuscriptSubmissionDeadline),
    ManuscriptSubmissionExtendedDeadline: String(
      rt2025.ManuscriptSubmissionExtendedDeadline
    ),
    StageId: rt2025.StageId,
    TopicCoordinators: sortedCoordinators.map(mapAuthor2025ToLegacy),
    TopicEditors: sortedEditors.map(mapAuthor2025ToLegacy),
    CoverImageUrl: rt2025.CoverImageUrl
  };
}

/**
 * Maps a 2025 basic info schema to the legacy basic info schema format
 */
export function mapResearchTopicBasicInfo2025ToLegacy(
  rt2025: IResearchTopicBasicInfo2025Schema
): IResearchTopicBasicInfoSchema {
  return {
    ResearchTopicId: rt2025.ResearchTopicId,
    Slug: rt2025.Slug,
    Title: rt2025.Title,
    IsMagazinePage: rt2025.IsMagazinePage
  };
}

/** Replicator2025 fields loaded for impact tracking. People and page fields stay out. */
export interface IResearchTopic2025ImpactSource {
  ResearchTopicId: number;
  Journals?: Array<Pick<IRTJournal2025Schema, "JournalId"> | null> | null;
}

export interface IResearchTopicImpactSource {
  ResearchTopicId: number;
  JournalIds: number[];
}

/**
 * Maps a Replicator2025 impact projection to the topic id and journal ids.
 * Does not read coordinators, editors, or other page fields.
 */
export function mapResearchTopic2025ToImpactSource(
  rt2025: IResearchTopic2025ImpactSource
): IResearchTopicImpactSource {
  const journalIds = (rt2025.Journals ?? []).flatMap((journal) =>
    typeof journal?.JournalId === "number" ? [journal.JournalId] : []
  );

  return {
    ResearchTopicId: rt2025.ResearchTopicId,
    JournalIds: journalIds
  };
}
